import { ChapterParams } from '@/data/services/chapterServices';
import {
  createSubchapter,
  deleteSubchapter,
  updateSubchapter,
} from '@/data/services/subchapterServices';
import { TempChapter } from '@/models/Chapter';
import { TempLesson } from '@/models/Lesson';
import { TempSubChapter } from '@/models/Subchapter';
import { buildChangedObject } from '../buildChangedObject';
import { separeArrayStudioActions } from '@/pages/admin/courses/studio/info/CourseStudioInfo.hooks';

export async function performCreateSubchapter(
  lessonId: number,
  bookId: number,
  chapterId: number,
  subchapter: TempSubChapter,
) {
  const params: ChapterParams = {
    lessonId,
    bookType: 'STUDENT',
    bookId,
    chapterId,
  };

  const body = {
    title: subchapter.title,
    chapterId,
  };

  const createdSubchapter = await createSubchapter(params, body);

  return createdSubchapter;
}
export async function performCreateSubchapters(
  lessonId: number,
  chapter: TempChapter,
) {
  const createdSubchapters: TempSubChapter[] = [];

  const items = chapter.items || [];

  for (const subchapter of items) {
    const createdSubchapter = await performCreateSubchapter(
      lessonId,
      chapter.book,
      chapter.id,
      subchapter,
    );
    createdSubchapters.push(createdSubchapter);
  }

  return createdSubchapters;
}
export async function performCreateSubchaptersIntoChapters(
  lessonId: number,
  chapters: TempChapter[],
) {
  const createdSubchapters: TempChapter[] = [];

  for (const chapter of chapters) {
    const createdSubchapter = await performCreateSubchapters(lessonId, chapter);
    chapter.items = createdSubchapter;
    createdSubchapters.push(chapter);
  }

  return createdSubchapters;
}
export async function performCreateSubchaptersIntoLessons(
  lessons: TempLesson[],
) {
  const createdLessons: TempLesson[] = [];

  for (const lesson of lessons) {
    const createdChapters = await performCreateSubchaptersIntoChapters(
      lesson.id,
      lesson.chapters || [],
    );

    lesson.chapters = createdChapters;
    createdLessons.push(lesson);
  }

  return createdLessons;
}
export async function performUpdateLessonSubchapters(
  currentLessons: TempLesson[],
  newLessons: TempLesson[],
): Promise<TempLesson[]> {
  const currentLessonsMap = new Map(currentLessons.map(l => [l.id, l]));
  const updatedLessons: TempLesson[] = [];

  for (const newLesson of newLessons) {
    const currentLesson = currentLessonsMap.get(newLesson.id);

    const updatedChapters = await performUpdateChapterSubchapters(
      currentLesson?.chapters || [],
      newLesson.chapters || [],
      newLesson.id,
      newLesson.book?.id,
    );

    newLesson.chapters = updatedChapters;
    updatedLessons.push(newLesson);
  }

  return updatedLessons;
}

function hasValidIds(bookId?: number, chapterId?: number): boolean {
  return Boolean(bookId && chapterId);
}
async function performUpdateSubchapters(
  currentSubchapters: TempSubChapter[],
  newSubchapters: TempSubChapter[],
  lessonId: number,
  bookId?: number,
  chapterId?: number,
): Promise<TempSubChapter[]> {
  if (!hasValidIds(bookId, chapterId)) return newSubchapters;

  const validBookId = bookId || 0;
  const validChapterId = chapterId || 0;

  const { arrayToDelete, arrayToUpdate, arrayToCreate } =
    separeArrayStudioActions(currentSubchapters, newSubchapters);

  await performDeleteSubchapters(
    arrayToDelete,
    lessonId,
    validBookId,
    validChapterId,
  );

  const currentSubchaptersMap = new Map(currentSubchapters.map(s => [s.id, s]));

  const updatedSubchapters: TempSubChapter[] = [];
  for (const newSubchapter of arrayToUpdate) {
    const currentSubchapter = currentSubchaptersMap.get(newSubchapter.id);
    if (currentSubchapter) {
      const updatedSubchapter = await performUpdateSubchapter(
        currentSubchapter,
        newSubchapter,
        lessonId,
        validBookId,
        validChapterId,
      );
      updatedSubchapters.push(updatedSubchapter);
    }
  }

  const createdSubchapters: TempSubChapter[] = [];
  for (const newSubchapter of arrayToCreate) {
    const createdSubchapter = await createSubchapter(
      {
        lessonId,
        bookType: 'STUDENT',
        bookId: validBookId,
        chapterId: validChapterId,
      },
      { title: newSubchapter.title, chapterId: validChapterId },
    );
    createdSubchapters.push(createdSubchapter);
  }

  return [...updatedSubchapters, ...createdSubchapters];
}

export async function performUpdateChapterSubchapters(
  currentChapters: TempChapter[],
  newChapters: TempChapter[],
  lessonId: number,
  bookId?: number,
): Promise<TempChapter[]> {
  const currentChaptersMap = new Map(currentChapters.map(c => [c.id, c]));
  const updatedChapters: TempChapter[] = [];

  for (const newChapter of newChapters) {
    const currentChapter = currentChaptersMap.get(newChapter.id);

    const updatedSubchapters = await performUpdateSubchapters(
      currentChapter?.items || [],
      newChapter.items || [],
      lessonId,
      bookId,
      newChapter.id,
    );

    newChapter.items = updatedSubchapters;
    updatedChapters.push(newChapter);
  }

  return updatedChapters;
}
export async function performUpdateSubchapter(
  currentSubchapter: TempSubChapter,
  newSubchapter: TempSubChapter,
  lessonId: number,
  bookId?: number,
  chapterId?: number,
): Promise<TempSubChapter> {
  if (!bookId || !chapterId) return newSubchapter;

  const changes = buildChangedObject(currentSubchapter, newSubchapter, {
    ignoreFields: ['tempId'],
  });

  if (Object.keys(changes).length === 0) return newSubchapter;

  const updatedSubchapter = await updateSubchapter(
    {
      lessonId,
      bookType: 'STUDENT',
      bookId,
      chapterId,
      subchapterId: newSubchapter.id,
    },
    changes,
  );

  return updatedSubchapter;
}

export async function performDeleteSubchapters(
  subchapters: TempSubChapter[],
  lessonId: number,
  bookId?: number,
  chapterId?: number,
): Promise<void> {
  for (const subchapter of subchapters) {
    if (subchapter.id && bookId && chapterId) {
      await deleteSubchapter({
        lessonId,
        bookType: 'STUDENT',
        bookId,
        chapterId,
        subchapterId: subchapter.id,
      });
    }
  }
}
